import { useFormikContext } from 'formik'
import get from 'lodash/get'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Grid, H, P, Wrapper } from '@farewill/ui'
import { COLOR } from '@farewill/ui/tokens'

import { FakePaymentButton } from 'components/fake-payment-button'
import RadioGroup from 'components/form/radio-group'
import { PaymentProviderCard } from 'components/payment-provider-card'
import { TelephonePaymentNote } from 'components/telephone-payment-note'
import SectionStatus from 'components/section-status'
import ENV from 'config/environment'
import { formatCurrency, hasDecimals } from 'lib/formatting/currency'
import { launchPciPalSession } from 'lib/pci-pal/launch-session'
import { FuneralPlanLeadAttributes, Lead } from 'lib/models/lead'
import { QuoteResponseDataFuneralPlanAttributes } from 'lib/types'
import { generatePciPalSession } from 'state/actions'
import { LEAD_STATUS } from 'utils/enums'

import { useLeadContext } from '../context'
import { PaymentTaken } from './payment-taken'
import { PaymentLinkDetails } from './payment-link-details'

/** The payment note will NOT appear if:
 * 1. There is no quote; or
 * 2. The customer has paid but the lead is not converted; or
 * 3. There is an unpaid payment-link invoice.
 * The payment note WILL appear if the lead is converted, or the customer has not yet paid (with a quote). */
const PaymentNote = ({
  isConverted,
  takingPayment,
  paymentSystemUsed,
  hasUnpaidPaymentLinkInvoice = false,
  quote,
}: {
  isConverted: boolean
  takingPayment: boolean
  paymentSystemUsed?: 'PCI PAL'
  hasUnpaidPaymentLinkInvoice?: boolean
  quote?: QuoteResponseDataFuneralPlanAttributes
}) => {
  return !quote ||
    (quote.customerPaymentDetails.amountPaidInPence > 0 && !isConverted) ||
    hasUnpaidPaymentLinkInvoice ? (
    <></>
  ) : (
    <TelephonePaymentNote
      isConverted={isConverted}
      takingPayment={takingPayment}
      paymentSystemUsed={paymentSystemUsed}
      paymentButtonLabel="Pay with PCI PAL"
    />
  )
}

const PaymentQuestions = ({
  quote,
}: {
  quote?: QuoteResponseDataFuneralPlanAttributes
}): React.ReactElement => {
  const { values, handleSubmit } = useFormikContext<FuneralPlanLeadAttributes>()
  const [takingPayment, setTakingPayment] = useState<boolean>(false)
  const [paymentSystemUsed, setPaymentSystemUsed] = useState<
    'PCI PAL' | undefined
  >(undefined)
  const dispatch = useDispatch()
  const { lead, fetchLead } = useLeadContext()
  const isConverted = lead.attributes.status === LEAD_STATUS.CONVERTED

  const customerHasMadeAPayment = quote
    ? quote.customerPaymentDetails.amountPaidInPence > 0
    : false

  const hasUnpaidPaymentLinkInvoice =
    quote?.customerPaymentDetails.hasUnpaidPaymentLinkInvoice ?? false

  const blockTakingPayment =
    customerHasMadeAPayment || hasUnpaidPaymentLinkInvoice

  const showFakePaymentButton =
    !isConverted && !blockTakingPayment && ENV.APP_ENV !== 'production'

  const handlePaymentButton = async ({
    leadUuid,
    paymentType,
    amountInPence,
  }: {
    leadUuid: Lead['attributes']['uuid']
    paymentType: FuneralPlanLeadAttributes['quoteAttributes']['paymentType']
    amountInPence: number
  }): Promise<void> => {
    setTakingPayment(true)
    setPaymentSystemUsed('PCI PAL')
    await openPciPalTab({
      referenceId: leadUuid,
      paymentType,
      amountInPence,
    })
  }

  const openPciPalTab = async ({
    referenceId,
    paymentType,
    amountInPence,
  }: {
    referenceId: Lead['attributes']['uuid']
    paymentType: FuneralPlanLeadAttributes['quoteAttributes']['paymentType']
    amountInPence: number
  }): Promise<void> => {
    try {
      const response = await dispatch(
        generatePciPalSession({
          referenceId,
          paymentType,
          amountInPence,
          product: 'funeral_plan',
        })
      )

      const data = get(response, 'value.data.data')
      const url = get(data, 'url')
      const accessToken = get(data, 'accessToken')
      const refreshToken = get(data, 'refreshToken')

      launchPciPalSession({ url, accessToken, refreshToken })
    } catch {
      // Backstage error handling will surface details to the user
    }
  }

  /** Assumes for now that there is just one celebration item per plan */
  const celebrationItem = quote?.adjustments?.filter(
    (a) => a.type === 'item.celebration'
  )[0]

  return (
    <>
      <H size="S">Payment</H>
      <Grid>
        {!isConverted && (
          <Grid.Item>
            <RadioGroup
              name="quoteAttributes.paymentType"
              options={[
                { label: 'In monthly instalments', value: 'instalments' },
                { label: 'Upfront', value: 'in_full' },
              ]}
              label="How would they like to pay?"
              handleSave={() => handleSubmit()}
            />
          </Grid.Item>
        )}

        {quote && customerHasMadeAPayment && (
          <Grid.Item>
            <PaymentTaken
              amountPaidInPence={quote.customerPaymentDetails.amountPaidInPence}
              amountLeftToPayForConversionInPence={
                quote.customerPaymentDetails.amountLeftToPayForConversionInPence
              }
              paymentType={quote.paymentType}
            />
          </Grid.Item>
        )}
        {quote?.paymentType === 'in_full' && (
          <>
            <Grid.Item>
              <SectionStatus heading="Price in full" complete>
                <P color={COLOR.BLACK} size="L">
                  Price to pay:{' '}
                  {formatCurrency({
                    showPence: hasDecimals(
                      quote.totalPayableAmountInPence / 100
                    ),
                    value: quote.totalPayableAmountInPence,
                    valueInPence: true,
                  })}
                </P>
                <P color={COLOR.BLACK}>
                  Funeral Plan price:{' '}
                  {formatCurrency({
                    showPence: hasDecimals(quote.packagePriceInPence / 100),
                    value: quote.packagePriceInPence,
                    valueInPence: true,
                  })}
                </P>
                {celebrationItem && (
                  <P color={COLOR.BLACK}>
                    Celebration:{' '}
                    {formatCurrency({
                      showPence: hasDecimals(
                        celebrationItem.amountInPence / 100
                      ),
                      value: celebrationItem.amountInPence,
                      valueInPence: true,
                    })}
                  </P>
                )}
                <P color={COLOR.BLACK}>
                  Discount: -
                  {formatCurrency({
                    showPence: hasDecimals(
                      quote.totalDiscountAmountInPence / 100
                    ),
                    value: quote.totalDiscountAmountInPence,
                    valueInPence: true,
                  })}
                </P>
              </SectionStatus>
            </Grid.Item>
            {quote && !isConverted && !blockTakingPayment && (
              <Grid.Item>
                <PaymentProviderCard
                  onPciPalClick={async () =>
                    await handlePaymentButton({
                      leadUuid: values.uuid,
                      paymentType: quote.paymentType,
                      amountInPence:
                        quote.customerPaymentDetails
                          .amountLeftToPayForConversionInPence,
                    })
                  }
                />
              </Grid.Item>
            )}
            {showFakePaymentButton && (
              <FakePaymentButton
                leadId={lead.id}
                onPaymentCreated={() => fetchLead()}
                paymentType={quote.paymentType}
                amountInPence={
                  quote.customerPaymentDetails
                    .amountLeftToPayForConversionInPence
                }
              />
            )}
          </>
        )}
        {quote?.paymentType === 'instalments' && (
          <>
            <Grid.Item>
              <SectionStatus heading="Price for monthly instalments" complete>
                <P color={COLOR.BLACK} size="L">
                  {formatCurrency({
                    showPence: hasDecimals(
                      quote.monthlyPayableAmountInPence / 100
                    ),
                    value: quote.monthlyPayableAmountInPence,
                    valueInPence: true,
                  })}{' '}
                  per month for {quote.numberOfInstalments} months
                </P>
                <P color={COLOR.BLACK}>
                  Funeral Plan price:{' '}
                  {formatCurrency({
                    showPence: hasDecimals(quote.packagePriceInPence / 100),
                    value: quote.packagePriceInPence,
                    valueInPence: true,
                  })}
                </P>
                {celebrationItem && (
                  <P color={COLOR.BLACK}>
                    Celebration:{' '}
                    {formatCurrency({
                      showPence: hasDecimals(
                        celebrationItem.amountInPence / 100
                      ),
                      value: celebrationItem.amountInPence,
                      valueInPence: true,
                    })}
                  </P>
                )}
                <P color={COLOR.BLACK}>
                  Instalment fee:{' '}
                  {formatCurrency({
                    showPence: hasDecimals(quote.instalmentsFeeInPence / 100),
                    value: quote.instalmentsFeeInPence,
                    valueInPence: true,
                  })}
                </P>
                <P color={COLOR.BLACK}>
                  Discount: {quote.totalDiscountAmountInPence > 0 ? '-' : ''}
                  {formatCurrency({
                    showPence: hasDecimals(
                      quote.totalDiscountAmountInPence / 100
                    ),
                    value: quote.totalDiscountAmountInPence,
                    valueInPence: true,
                  })}
                </P>
              </SectionStatus>
            </Grid.Item>

            {!isConverted && !blockTakingPayment && (
              <>
                <Grid.Item>
                  <Wrapper margin={['M', 0, 0]}>
                    <PaymentProviderCard
                      onPciPalClick={async () =>
                        await handlePaymentButton({
                          leadUuid: values.uuid,
                          paymentType: quote.paymentType,
                          amountInPence:
                            quote.customerPaymentDetails
                              .amountLeftToPayForConversionInPence,
                        })
                      }
                    />
                  </Wrapper>
                </Grid.Item>
              </>
            )}
            {showFakePaymentButton && (
              <FakePaymentButton
                leadId={lead.id}
                onPaymentCreated={() => fetchLead()}
                paymentType={quote.paymentType}
                amountInPence={
                  quote.customerPaymentDetails
                    .amountLeftToPayForConversionInPence
                }
              />
            )}
          </>
        )}
        <Grid.Item>
          <PaymentNote
            isConverted={isConverted}
            takingPayment={takingPayment}
            paymentSystemUsed={paymentSystemUsed}
            hasUnpaidPaymentLinkInvoice={hasUnpaidPaymentLinkInvoice}
            quote={quote}
          />
        </Grid.Item>
        {quote && !customerHasMadeAPayment && !isConverted && (
          <Grid.Item>
            <PaymentLinkDetails
              hasUnpaidPaymentLinkInvoice={hasUnpaidPaymentLinkInvoice}
              leadUuid={values.uuid}
              paymentType={quote.paymentType}
              amountInPence={
                quote.customerPaymentDetails.amountLeftToPayForConversionInPence
              }
            />
          </Grid.Item>
        )}
      </Grid>
    </>
  )
}

export default PaymentQuestions
